import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ARIAL_FONT } from 'pxc-editor-material-svg-rendering/constants';
import { handlePrintClick } from 'pxc-editor-web/environment_modules/common/actions/print';
import { handleCustomerProjectDebugClick } from 'pxc-editor-web/environment_modules/web_component/actions/project';
import { colors } from '@/globalStyles';
import { isWebComponentEnvironment, getCurrentEnvironment } from '@/helpers';
import { printers } from '@/helpers/printers';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { APP_ENVIRONMENT, USER_SIZE_UNIT_MM, USER_SIZE_UNIT_INCH } from '@/constants';
import {
  setContentPlacingDebuggingState,
  setDebuggingGridState,
  setUserSizeUnit,
  setVariantColorModalOpen
} from '@/actions';
import { toggleFullscreenMode } from '@/store/actions/cmme';
import { handleMatfileUpload, handleProjectFileUpload, handleSaveProjectClick } from 'EnvironmentModule/actions/project';
import { ContextMenuStyled, ContextMenuItemStyled } from '../styles';
import { SizeUnitsItemStyled } from './styles';

const currentEnvironment = getCurrentEnvironment();

const Separator = () => {
  return (
    <Box sx={{ marginBottom: '10px', padding: '0px 25px' }}>
      <Box sx={{ height: '1px', width: '100%', backgroundColor: colors.whiteSmoke }} />
    </Box>
  );
};

const DotsContextMenu = props => {
  const {
    filteredLanguage,
    handleClose,
    handleArticleNumberModalOpen,
    handleSaveAsModalOpen,
    handleLanguageModalOpen,
    handlePrinterModalOpen,
    handleInputTypeFileSelection,
    handleFontSizeCoefModalOpen,
    handleLoadCFWByArticleNumberModalOpen,
  } = props;
  const pages = useSelector((state) => state.core.pages);
  const isContentPlacingDebugging = useSelector((state) => state.core.isContentPlacingDebugging);
  const isDebuggingGrid = useSelector((state) => state.core.isDebuggingGrid);
  const userSizeUnit = useSelector((state) => state.core.userSizeUnit);
  const fontSizeCoef = useSelector((state) => state.core.fontSizeCoef);
  const colorSchema = useSelector((state) => state.core.colorSchema);
  const preselectedPrinter = useSelector((state) => state.core.preselectedPrinter);
  const printerList = useSelector((state) => state.core.printerList);
  const dispatch = useDispatch();
  const fileInputRef = React.useRef(null);
  const webComponent = isWebComponentEnvironment();
  const matfileTemplate = getBuildedMatfileTemplate();
  const acceptFileExtensions = `application/json,.json${webComponent ? ',.cmme' : ',.mproj'}`;
  const isLoadProjectVisible = [APP_ENVIRONMENT.WEB_COMPONENT].includes(currentEnvironment);
  const isLoadProjectDisabled = ![APP_ENVIRONMENT.WEB_COMPONENT].includes(currentEnvironment);
  const isSaveProjectVisible = [APP_ENVIRONMENT.WEB_COMPONENT].includes(currentEnvironment);
  const isPrinterSelectionDisabled = !printerList.length;
  const isVariantColorSelectionDisabled = !matfileTemplate?.variants.length;
  const hasPages = Object.keys(pages).length > 0;
  const isSaveProjectDisabled = [APP_ENVIRONMENT.WEB_COMPONENT].includes(currentEnvironment) ? (hasPages ? false : true) : true;
  const debugMessTrns = isContentPlacingDebugging ? 'app.header.menu.debugContentPlacingOff' : 'app.header.menu.debugContentPlacingOn';
  const gridMessTrns = isDebuggingGrid ? 'app.controls.panel.gridOff' : 'app.controls.panel.gridOn';
  const onClickUserSizeUnit = userSizeUnit === USER_SIZE_UNIT_MM ? USER_SIZE_UNIT_INCH : USER_SIZE_UNIT_MM;
  const fontSizeFactor = fontSizeCoef[ARIAL_FONT];
  const currentPrinter = printers.find(p => p.id === preselectedPrinter);

  const handleOpenFile = e => {
    window.removeEventListener('focus', handleFocusBack);
    dispatch(handleMatfileUpload(e));
    handleInputTypeFileSelection(false);
    handleClose();
  };

  const handleArticleNumberModal = () => {
    handleArticleNumberModalOpen(true);
    handleClose();
  };

  const handleOpenProject = e => {
    dispatch(handleProjectFileUpload(e));
    handleInputTypeFileSelection(false);
    handleClose();
  };

  const handleSaveProject = () => {
    if (!isSaveProjectDisabled) {
      if (webComponent) {
        handleSaveAsModalOpen(true);
      } else {
        dispatch(handleSaveProjectClick());
      }
      handleClose();
    }
  };

  const handleSaveProjectByRequest = () => {
    if (!isSaveProjectDisabled) {
      dispatch(handleCustomerProjectDebugClick());
      handleClose();
    }
  };

  const handleDebugClick = value => {
    dispatch(setContentPlacingDebuggingState(value));
    handleClose();
  };

  const handleGridClick = value => {
    dispatch(setDebuggingGridState(value));
    handleClose();
  };

  const handlePrinterClick = () => {
    if (!isPrinterSelectionDisabled) {
      handlePrinterModalOpen(true);
      handleClose();
    }
  };

  const handleLanguageClick = () => {
    handleLanguageModalOpen(true);
    handleClose();
  };

  const handleSizeUnitClick = () => {
    dispatch(setUserSizeUnit(onClickUserSizeUnit));
    handleClose();
  };

  const handleInputTypeFileClick = e => {
    window.addEventListener('focus', handleFocusBack);
    handleInputTypeFileSelection(true);
  };

  const handleFocusBack = () => {
    window.removeEventListener('focus', handleFocusBack);
    handleInputTypeFileSelection(false);
  };

  const handleFontSizeFactorClick = () => {
    handleFontSizeCoefModalOpen(true);
    handleClose();
  };

  const handleSavePngClick = () => {
    dispatch(handlePrintClick());
  };

  const handleCreateCFMClick = () => {
    handleLoadCFWByArticleNumberModalOpen(true);
    handleClose();
  };

  const handleVariantColorClick = () => {
    if (!isVariantColorSelectionDisabled) {
      dispatch(setVariantColorModalOpen(true));
      handleClose();
    }
  };

  const handleFullScreenModeClick = () => {
    dispatch(toggleFullscreenMode());
    handleClose();
  }

  return (
    <ContextMenuStyled>
      <ContextMenuItemStyled>
        <label className="cursor-pointer" htmlFor="file-upload-input">
          <input
            ref={fileInputRef}
            style={{ display: 'none' }}
            id="file-upload-input"
            type="file"
            accept="application/json,.json"
            disabled={false}
            onChange={handleOpenFile}
            onClick={handleInputTypeFileClick}
          />
          <FormattedMessage id="app.header.menu.upload" defaultMessage="Upload" />
        </label>
      </ContextMenuItemStyled>

      {webComponent && (
        <ContextMenuItemStyled onClick={handleArticleNumberModal}>
          <FormattedMessage id="cmme.uploadViaUrl" defaultMessage="Open via URL" />
        </ContextMenuItemStyled>
      )}

      {isLoadProjectVisible && (
        <ContextMenuItemStyled $disabled={isLoadProjectDisabled}>
          <label {...(!isLoadProjectDisabled && { className: 'cursor-pointer' })} htmlFor="project-file-upload-input">
            <input
              style={{ display: 'none' }}
              id="project-file-upload-input"
              type="file"
              accept={acceptFileExtensions}
              disabled={isLoadProjectDisabled}
              onChange={handleOpenProject}
              onClick={handleInputTypeFileClick}
            />
            <FormattedMessage id="app.header.menu.loadProject" defaultMessage="Load Project" />
          </label>
        </ContextMenuItemStyled>
      )}

      <ContextMenuItemStyled onClick={handleCreateCFMClick}>
        <FormattedMessage id="debug.createCFW" defaultMessage="Create CFW" />
      </ContextMenuItemStyled>

      {isSaveProjectVisible && (
        <ContextMenuItemStyled $disabled={isSaveProjectDisabled} onClick={handleSaveProject}>
          <FormattedMessage id="app.header.menu.saveProject" defaultMessage="Save Project" />
        </ContextMenuItemStyled>
      )}

      {isSaveProjectVisible && (
        <ContextMenuItemStyled $disabled={isSaveProjectDisabled} onClick={handleSaveProjectByRequest}>
          <span>Save by request (simulation)</span>
        </ContextMenuItemStyled>
      )}

      <ContextMenuItemStyled onClick={handleSavePngClick}>
        <FormattedMessage
          id="debug.saveBitmap"
          defaultMessage="Save png (bitmap)"
        />
      </ContextMenuItemStyled>

      <Separator/>

      <ContextMenuItemStyled
        data-value="fullscreen-mode"
        onClick={handleFullScreenModeClick}
      >
        <FormattedMessage
          id="debug.fullScreenMode"
          defaultMessage="Full screen mode"
        />
      </ContextMenuItemStyled>

      {webComponent && (
        <ContextMenuItemStyled onClick={handleFontSizeFactorClick}>
          <span>
            <FormattedMessage id="debug.fontSizeFactor" defaultMessage="Font-Size factor" />: {fontSizeFactor}
          </span>
        </ContextMenuItemStyled>
      )}

      <ContextMenuItemStyled onClick={handleDebugClick.bind(null, !isContentPlacingDebugging)}>
        <FormattedMessage id={debugMessTrns} defaultMessage="Debug" />
      </ContextMenuItemStyled>

      <ContextMenuItemStyled onClick={handleGridClick.bind(null, !isDebuggingGrid)}>
        <FormattedMessage id={gridMessTrns} defaultMessage="Grid" />
      </ContextMenuItemStyled>

      <ContextMenuItemStyled $disabled={isPrinterSelectionDisabled} onClick={handlePrinterClick}>
        <span>{currentPrinter?.name}</span>
      </ContextMenuItemStyled>

      <ContextMenuItemStyled onClick={handleLanguageClick}>
        {filteredLanguage?.name || 'Language'}
      </ContextMenuItemStyled>

      <ContextMenuItemStyled $disabled={isVariantColorSelectionDisabled} onClick={handleVariantColorClick}>
        {isVariantColorSelectionDisabled || !colorSchema ? (
          <FormattedMessage id="debug.colorVariant" defaultMessage="Color variant" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '15px', height: '15px', borderRadius: '4px', backgroundColor: colorSchema.background_color_hex, marginRight: '10px', border: 'solid 1px #ccd0d2' }} />
            <span>{colorSchema.article_name}</span>
          </Box>
        )}
      </ContextMenuItemStyled>

      <ContextMenuItemStyled onClick={handleSizeUnitClick}>
        <SizeUnitsItemStyled $selected={userSizeUnit === USER_SIZE_UNIT_MM}>
          <FormattedMessage id="app.sizeUnits.mm" defaultMessage={USER_SIZE_UNIT_MM} />
        </SizeUnitsItemStyled>
        <Box>&nbsp;|&nbsp;</Box>
        <SizeUnitsItemStyled $selected={userSizeUnit === USER_SIZE_UNIT_INCH}>
          <FormattedMessage id="app.sizeUnits.inch" defaultMessage={USER_SIZE_UNIT_INCH} />
        </SizeUnitsItemStyled>
      </ContextMenuItemStyled>
    </ContextMenuStyled>
  );
};

export default DotsContextMenu;
